import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { errorMessage } from "./workApi";

/**
 * "Reports to" and main work area for an employee form (the department is a field of the employee
 * itself). The parent calls ``ref.current.save(userId)`` after the employee itself was saved.
 */
const ReportingLineFields = forwardRef(function ReportingLineFields(
  { api, userId, groupClassName = "form-group", labelClassName, SelectComponent = "select" },
  ref
) {
  const [tree, setTree] = useState(null);
  const [areas, setAreas] = useState([]);
  const [value, setValue] = useState({ Reports_To_User_Id: "", Primary_Area_Id: "" });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.orgTree(), api.areas()])
      .then(([t, a]) => {
        if (cancelled) return;
        setTree(t);
        setAreas(a || []);
        const me = userId ? (t?.members || []).find((m) => Number(m.User_Id) === Number(userId)) : null;
        if (me) {
          setValue({
            Reports_To_User_Id: me.Reports_To_User_Id || "",
            Primary_Area_Id: me.Primary_Area_Id || "",
          });
        }
      })
      .catch(() => !cancelled && setTree({ members: [] }));
    return () => {
      cancelled = true;
    };
  }, [api, userId]);

  useImperativeHandle(ref, () => ({
    /** Returns null on success or an error message. */
    async save(targetUserId) {
      if (!dirty || !targetUserId) return null;
      try {
        await api.updateOrgMember(targetUserId, {
          Reports_To_User_Id: value.Reports_To_User_Id || null,
          Primary_Area_Id: value.Primary_Area_Id || null,
        });
        setDirty(false);
        return null;
      } catch (err) {
        return errorMessage(err, "Could not save the reporting line");
      }
    },
  }));

  const set = (key) => (e) => {
    setValue((v) => ({ ...v, [key]: e.target.value }));
    setDirty(true);
  };

  if (!tree) return null;
  const members = tree.members || [];
  const me = userId ? members.find((m) => Number(m.User_Id) === Number(userId)) : null;
  // A person cannot report to themselves or to anyone in their own team.
  const blocked = new Set(userId ? [Number(userId)] : []);
  const stack = [...blocked];
  while (stack.length) {
    const bossId = stack.pop();
    for (const m of members) {
      const id = Number(m.User_Id);
      if (Number(m.Reports_To_User_Id) === bossId && !blocked.has(id)) {
        blocked.add(id);
        stack.push(id);
      }
    }
  }
  const managers = members
    .filter((m) => !blocked.has(Number(m.User_Id)))
    .sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));

  return (
    <>
      <div className={groupClassName}>
        <label className={labelClassName} htmlFor="rl-reports-to">
          Reports to
        </label>
        {me?.Is_Owner ? (
          <input id="rl-reports-to" value="Company owner (top of the org chart)" readOnly disabled />
        ) : (
          <SelectComponent id="rl-reports-to" value={value.Reports_To_User_Id} onChange={set("Reports_To_User_Id")}>
            <option value="">No manager (top level)</option>
            {managers.map((m) => (
              <option key={m.User_Id} value={m.User_Id}>
                {m.Name}
                {m.Designation ? ` · ${m.Designation}` : ""}
              </option>
            ))}
          </SelectComponent>
        )}
      </div>
      <div className={groupClassName}>
        <label className={labelClassName} htmlFor="rl-area">
          Main work area
        </label>
        <SelectComponent id="rl-area" value={value.Primary_Area_Id} onChange={set("Primary_Area_Id")}>
          <option value="">—</option>
          {areas.map((a) => (
            <option key={a.Area_Id} value={a.Area_Id}>
              {a.Area_Name}
            </option>
          ))}
        </SelectComponent>
      </div>
    </>
  );
});

export default ReportingLineFields;
